import React, { useState, useEffect, useContext, useCallback } from 'react';
import classNames from 'classnames/bind';
import styles from './PromotionLinkManagement.module.css';
import { ToastContext } from '~/components/context/ToastContext';
import { ModalContext } from '~/components/context/ModalContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSave, faEdit, faPlus, faCopy, faChartBar } from '@fortawesome/free-solid-svg-icons';
import { BASE_PROMOTION_URL, BASE_WEBSITE_URL } from '~/helper/apiService';

const cx = classNames.bind(styles);

function PromotionLinkManagement({ initialTab = 'links' }) {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [promotionsLoading, setPromotionsLoading] = useState(true);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [bookingsData, setBookingsData] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const { showToast } = useContext(ToastContext);
  const { showModal } = useContext(ModalContext);

  // Form state
  const [formData, setFormData] = useState({
    link_path: '',
    link_code: '',
    voucher_code: '',
    voucher_name: '',
    discount_type: 'none',
    discount_value: 0,
    description: '',
    is_active: true,
    start_date: '',
    end_date: '',
    max_uses: '',
  });

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);
  };

  // Fetch promotions
  const fetchPromotions = useCallback(async () => {
    try {
      setPromotionsLoading(true);
      const response = await fetch(`${BASE_PROMOTION_URL}`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        setPromotions(data.data || []);
        console.log(data.data)
      } else {
        showToast(data.error || 'Lỗi khi lấy danh sách promotion links', 'error');
      }
    } catch (err) {
      showToast('Lỗi kết nối đến server', 'error');
    } finally {
      setPromotionsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  // Fetch analytics summary
  const fetchAnalyticsSummary = useCallback(async () => {
    try {
      setAnalyticsLoading(true);
      const response = await fetch(
        `${BASE_PROMOTION_URL}/analytics/summary`,
        {
          credentials: 'include',
        }
      );
      const data = await response.json();
      if (response.ok) {
        setAnalyticsData(data.data || []);
      } else {
        showToast(data.error || 'Lỗi khi lấy thống kê', 'error');
      }
    } catch (err) {
      showToast('Lỗi kết nối đến server', 'error');
    } finally {
      setAnalyticsLoading(false);
    }
  }, [showToast]);

  // Fetch bookings
  const fetchBookings = useCallback(async (promotionId) => {
    try {
      setBookingsLoading(true);
      const params = new URLSearchParams();
      params.append('promotion_link_id', promotionId);

      const response = await fetch(
        `${BASE_PROMOTION_URL}/analytics/bookings?${params.toString()}`,
        {
          credentials: 'include',
        }
      );
      const data = await response.json();
      if (response.ok) {
        setBookingsData(data.data || []);
      } else {
        showToast(data.error || 'Lỗi khi lấy danh sách booking', 'error');
      }
    } catch (err) {
      showToast('Lỗi kết nối đến server', 'error');
    } finally {
      setBookingsLoading(false);
    }
  }, [showToast]);

  const handleViewAnalytics = (promotion) => {
    setSelectedPromotion(promotion);
    setShowAnalytics(true);
    fetchBookings(promotion.id);
  };

  const handleViewSummary = () => {
    setSelectedPromotion(null);
    setShowAnalytics(true);
    fetchAnalyticsSummary();
  };

  // Nếu mở tab "Thống kê" từ admin menu thì tự load luôn (không cần bấm nút)
  useEffect(() => {
    if (initialTab === 'stats') {
      handleViewSummary();
      return;
    }

    // initialTab === 'links'
    setShowAnalytics(false);
    setSelectedPromotion(null);
    setBookingsData([]);
    setAnalyticsData([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab]);

  const resetForm = () => {
    setFormData({
      link_path: '',
      link_code: '',
      voucher_code: '',
      voucher_name: '',
      discount_type: 'none',
      discount_value: 0,
      description: '',
      is_active: true,
      start_date: '',
      end_date: '',
      max_uses: '',
    });
    setEditingPromotion(null);
    setShowForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Tự động tạo link_path và link_code từ voucher_code khi thêm mới
    if (name === 'voucher_code' && value && !editingPromotion) {
      const voucherCodeLower = value.toLowerCase().replace(/\s+/g, '');
      const linkCode = voucherCodeLower;
      const linkPath = `?km=${linkCode}`;
      
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        link_code: linkCode,
        link_path: linkPath,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (promotion) => {
    // Không cho phép chỉnh sửa link default
    if (promotion.link_code === 'default') {
      showToast('Không thể chỉnh sửa link mặc định (default)', 'error');
      return;
    }

    setFormData({
      link_path: promotion.link_path || '',
      link_code: promotion.link_code || '',
      voucher_code: promotion.voucher_code || '',
      voucher_name: promotion.voucher_name || '',
      discount_type: promotion.discount_type || 'none',
      // Hiển thị giá trị giảm giá không có .00 (ví dụ: 10 thay vì 10.00)
      discount_value:
        promotion.discount_value !== null && promotion.discount_value !== undefined
          ? String(parseFloat(promotion.discount_value))
          : 0,
      description: promotion.description || '',
      is_active: promotion.is_active !== undefined ? promotion.is_active : true,
      start_date: formatDateInput(promotion.start_date),
      end_date: formatDateInput(promotion.end_date),
      max_uses: promotion.max_uses || '',
    });
    setEditingPromotion(promotion);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.link_path || !formData.link_code) {
      showToast('Link path và Link code là bắt buộc', 'error');
      return;
    }

    if (formData.discount_type !== 'none' && !formData.discount_value) {
      showToast('Vui lòng nhập giá trị giảm giá', 'error');
      return;
    }

    if (formData.start_date && formData.end_date) {
      if (new Date(formData.start_date) > new Date(formData.end_date)) {
        showToast('Ngày bắt đầu phải nhỏ hơn hoặc bằng ngày kết thúc', 'error');
        return;
      }
    }

    try {
      setLoading(true);
      const url = editingPromotion
        ? `${BASE_PROMOTION_URL}/${editingPromotion.id}`
        : `${BASE_PROMOTION_URL}`;
      const method = editingPromotion ? 'PUT' : 'POST';

      const payload = {
        ...formData,
        discount_value: formData.discount_type === 'none' ? 0 : parseFloat(formData.discount_value),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
      };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.ok) {
        showToast(
          editingPromotion
            ? 'Cập nhật promotion link thành công'
            : 'Tạo promotion link thành công',
          'success'
        );
        resetForm();
        fetchPromotions();
      } else {
        showToast(data.error || 'Lỗi khi lưu promotion link', 'error');
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (promotion) => {
    // Không cho phép xóa link default
    if (promotion.link_code === 'default') {
      showToast('Không thể xóa link mặc định (default)', 'error');
      return;
    }

    showModal({
      title: 'Xác nhận xóa',
      message: `Bạn có chắc chắn muốn xóa promotion link "${promotion.link_code}"?`,
      onConfirm: async () => {
        try {
          setLoading(true);
          const response = await fetch(`${BASE_PROMOTION_URL}/${promotion.id}`, {
            method: 'DELETE',
            credentials: 'include',
          });

          const data = await response.json();
          if (response.ok) {
            showToast('Xóa promotion link thành công', 'success');
            fetchPromotions();
          } else {
            showToast(data.error || 'Lỗi khi xóa promotion link', 'error');
          }
        } catch (error) {
          showToast('Lỗi kết nối: ' + error.message, 'error');
        } finally {
          setLoading(false);
        }
      },
      onCancel: null,
    });
  };

  const getFullUrl = (linkPath) => {
    if (!linkPath) return '';
    // Nếu link path đã có http/https thì giữ nguyên, nếu không thì thêm BASE_WEBSITE_URL
    if (linkPath.startsWith('http://') || linkPath.startsWith('https://')) {
      return linkPath;
    }
    return `${BASE_WEBSITE_URL}${linkPath}`;
  };

  const copyToClipboard = (text, isLinkPath = false) => {
    const textToCopy = isLinkPath ? getFullUrl(text) : text;
    navigator.clipboard.writeText(textToCopy);
    showToast('Đã copy vào clipboard', 'success');
  };

  const getDiscountDisplay = (promotion) => {
    if (promotion.discount_type === 'none') return 'Không có';
    if (promotion.discount_type === 'percentage') {
      return `${promotion.discount_value}%`;
    }
    return `${parseInt(promotion.discount_value).toLocaleString('vi-VN')}đ`;
  };

  if (promotionsLoading) {
    return <div className={cx('loading')}>Đang tải...</div>;
  }

  return (
    <div className={cx('container')}>
      <div className={cx('header')}>
        <h2>{initialTab === 'stats' ? 'Thống kê Link khuyến mại' : 'Quản lý Link khuyến mại'}</h2>
        <div className={cx('header-actions')}>
          {initialTab === 'stats' ? (
            <button className={cx('btn', 'btn-secondary')} onClick={fetchAnalyticsSummary} disabled={analyticsLoading}>
              <FontAwesomeIcon icon={faChartBar} /> Làm mới thống kê
            </button>
          ) : (
            <button className={cx('btn', 'btn-primary')} onClick={handleAdd}>
              <FontAwesomeIcon icon={faPlus} /> Thêm mới
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <div className={cx('modal-overlay')} onClick={resetForm}>
          <div className={cx('modal-form-container')} onClick={(e) => e.stopPropagation()}>
            <div className={cx('modal-form-header')}>
              <h3>{editingPromotion ? 'Chỉnh sửa' : 'Thêm mới'} Promotion Link</h3>
              <button
                className={cx('modal-close-btn')}
                onClick={resetForm}
                type="button"
                title="Đóng"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit}>
            <div className={cx('form-row')}>
              <div className={cx('form-group')}>
                <label>Mã Voucher</label>
                <input
                  type="text"
                  name="voucher_code"
                  value={formData.voucher_code}
                  onChange={handleInputChange}
                  placeholder="XANH19_TET"
                />
              </div>
              <div className={cx('form-group')}>
                <label>Tên Voucher</label>
                <input
                  type="text"
                  name="voucher_name"
                  value={formData.voucher_name}
                  onChange={handleInputChange}
                  placeholder="Khuyến mại mùa hè 20%"
                />
              </div>
            </div>

            <div className={cx('form-row')}>
              <div className={cx('form-group')}>
                <label>Đường dẫn Link *</label>
                <input
                  type="text"
                  name="link_path"
                  value={formData.link_path}
                  onChange={handleInputChange}
                  placeholder="?km=xanh19_tet"
                  required
                />
              </div>
              <div className={cx('form-group')}>
                <label>Mã Link *</label>
                <input
                  type="text"
                  name="link_code"
                  value={formData.link_code}
                  onChange={handleInputChange}
                  placeholder="xanh19_tet"
                  required
                />
              </div>
            </div>

            <div className={cx('form-row')}>
              <div className={cx('form-group')}>
                <label>Loại giảm giá</label>
                <select
                  name="discount_type"
                  value={formData.discount_type}
                  onChange={handleInputChange}
                >
                  <option value="none">Không có</option>
                  <option value="percentage">Phần trăm (%)</option>
                  <option value="fixed">Số tiền cố định (VNĐ)</option>
                </select>
              </div>
              {formData.discount_type !== 'none' && (
                <div className={cx('form-group')}>
                  <label>Giá trị giảm giá *</label>
                  <input
                    type="number"
                    name="discount_value"
                    value={formData.discount_value}
                    onChange={handleInputChange}
                    min="0"
                    step={formData.discount_type === 'percentage' ? '1' : '1000'}
                    required
                  />
                </div>
              )}
            </div>

            <div className={cx('form-row')}>
              <div className={cx('form-group')}>
                <label>Ngày bắt đầu</label>
                <input
                  type="datetime-local"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                />
              </div>
              <div className={cx('form-group')}>
                <label>Ngày kết thúc</label>
                <input
                  type="datetime-local"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className={cx('form-row')}>
              <div className={cx('form-group')}>
                <label>Số lần sử dụng tối đa</label>
                <input
                  type="number"
                  name="max_uses"
                  value={formData.max_uses}
                  onChange={handleInputChange}
                  min="1"
                  placeholder="Để trống = không giới hạn"
                />
              </div>
              <div className={cx('form-group')}>
                <label>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  Kích hoạt
                </label>
              </div>
            </div>

            <div className={cx('form-group')}>
              <label>Mô tả</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                placeholder="Mô tả về khuyến mại..."
              />
            </div>

            <div className={cx('form-actions')}>
              <button type="submit" className={cx('btn', 'btn-primary')} disabled={loading}>
                <FontAwesomeIcon icon={faSave} /> {editingPromotion ? 'Cập nhật' : 'Tạo mới'}
              </button>
              <button
                type="button"
                className={cx('btn', 'btn-secondary')}
                onClick={resetForm}
                disabled={loading}
              >
                Hủy
              </button>
            </div>
            </form>
          </div>
        </div>
      )}

      {initialTab !== 'stats' && (
      <div className={cx('table-container')}>
        <table className={cx('table')}>
          <thead>
            <tr>
              <th>Link Code</th>
              <th>Link Path</th>
              <th>Voucher</th>
              <th>Giảm giá</th>
              <th>Trạng thái</th>
              <th>Đã dùng</th>
              <th>Thời gian</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {promotions.length === 0 ? (
              <tr>
                <td colSpan="8" className={cx('empty')}>
                  Chưa có promotion link nào
                </td>
              </tr>
            ) : (
              promotions.map((promotion) => (
                <tr key={promotion.id}>
                  <td>
                    <strong>{promotion.link_code}</strong>
                    <button
                      className={cx('copy-btn')}
                      onClick={() => copyToClipboard(promotion.link_code)}
                      title="Copy link code"
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                  </td>
                  <td>
                    <div className={cx('link-path-container')}>
                      <span className={cx('link-path')}>{promotion.link_path}</span>
                      <div className={cx('full-url')}>
                        <small>Full URL: {getFullUrl(promotion.link_path)}</small>
                      </div>
                    </div>
                    <button
                      className={cx('copy-btn')}
                      onClick={() => copyToClipboard(promotion.link_path, true)}
                      title="Copy full URL"
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                    <a
                      href={getFullUrl(promotion.link_path)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cx('open-link-btn')}
                    >
                      Mở
                    </a>
                  </td>
                  <td>
                    {promotion.voucher_code ? (
                      <div>
                        <strong>{promotion.voucher_code}</strong>
                        {promotion.voucher_name && (
                          <div className={cx('voucher-name')}>{promotion.voucher_name}</div>
                        )}
                      </div>
                    ) : (
                      <span className={cx('no-voucher')}>-</span>
                    )}
                  </td>
                  <td>{getDiscountDisplay(promotion)}</td>
                  <td>
                    <span
                      className={cx('badge', {
                        active: promotion.is_active,
                        inactive: !promotion.is_active,
                      })}
                    >
                      {promotion.is_active ? 'Kích hoạt' : 'Tắt'}
                    </span>
                  </td>
                  <td>
                    {promotion.max_uses
                      ? `${promotion.current_uses || 0}/${promotion.max_uses}`
                      : `${promotion.current_uses || 0} (không giới hạn)`}
                  </td>
                  <td>
                    {promotion.start_date && (
                      <div>Bắt đầu: {formatDate(promotion.start_date)}</div>
                    )}
                    {promotion.end_date && (
                      <div>Kết thúc: {formatDate(promotion.end_date)}</div>
                    )}
                    {!promotion.start_date && !promotion.end_date && <span>-</span>}
                  </td>
                  <td>
                    <div className={cx('actions')}>
                      <button
                        className={cx('btn', 'btn-icon')}
                        onClick={() => handleViewAnalytics(promotion)}
                        title="Xem thống kê"
                      >
                        <FontAwesomeIcon icon={faChartBar} />
                      </button>
                      {promotion.link_code !== 'default' && (
                        <button
                          className={cx('btn', 'btn-icon')}
                          onClick={() => handleEdit(promotion)}
                          title="Chỉnh sửa"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                      )}
                      {promotion.link_code !== 'default' && (
                        <button
                          className={cx('btn', 'btn-icon', 'btn-danger')}
                          onClick={() => handleDelete(promotion)}
                          title="Xóa"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}

      {showAnalytics && (
        <div className={cx('analytics-container')}>
          <div className={cx('analytics-header')}>
            <h3>
              {selectedPromotion
                ? `Thống kê: ${selectedPromotion.link_code}`
                : 'Thống kê tổng hợp'}
            </h3>
            {initialTab !== 'stats' && (
              <button
                className={cx('btn', 'btn-secondary')}
                onClick={() => {
                  setShowAnalytics(false);
                  setSelectedPromotion(null);
                  setBookingsData([]);
                  setAnalyticsData([]);
                }}
              >
                Đóng
              </button>
            )}
          </div>

          {selectedPromotion ? (
            <div className={cx('bookings-section')}>
              <h4>Danh sách Bookings</h4>
              {bookingsLoading ? (
                <div className={cx('loading')}>Đang tải...</div>
              ) : bookingsData.length === 0 ? (
                <div className={cx('empty')}>Chưa có booking nào</div>
              ) : (
                <div className={cx('table-container')}>
                  <table className={cx('table')}>
                    <thead>
                      <tr>
                        <th>STT</th>
                        <th>Số điện thoại</th>
                        <th>Ngày đặt</th>
                        <th>Voucher</th>
                        <th>Thông tin booking</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookingsData.map((booking, index) => {
                        let bookingInfo = null;
                        try {
                          bookingInfo =
                            typeof booking.booking_info === 'string'
                              ? JSON.parse(booking.booking_info)
                              : booking.booking_info || {};
                        } catch (e) {
                          console.error('Lỗi parse booking_info:', e);
                          bookingInfo = {};
                        }
                        return (
                          <tr key={booking.id}>
                            <td>{index + 1}</td>
                            <td>{booking.phone || '-'}</td>
                            <td>{formatDate(booking.booking_date)}</td>
                            <td>
                              <strong>{booking.voucher_code || '-'}</strong>
                            </td>
                            <td>
                              <div className={cx('booking-info')}>
                                <div>Điểm đón: {bookingInfo?.pickup || '-'}</div>
                                <div>Điểm trả: {bookingInfo?.dropoff || '-'}</div>
                                <div>
                                  Ngày đi: {bookingInfo?.date || '-'} {bookingInfo?.time ? `- ${bookingInfo.time}` : ''}
                                </div>
                                <div>Số người: {bookingInfo?.passengers || '-'}</div>
                                {bookingInfo?.note && (
                                  <div className={cx('booking-note')}>Ghi chú: {bookingInfo.note}</div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div className={cx('summary-section')}>
              <h4>Thống kê tổng hợp</h4>
              {analyticsLoading ? (
                <div className={cx('loading')}>Đang tải...</div>
              ) : analyticsData.length === 0 ? (
                <div className={cx('empty')}>Chưa có dữ liệu thống kê</div>
              ) : (
                <div className={cx('table-container')}>
                  <table className={cx('table')}>
                    <thead>
                      <tr>
                        <th>Link Code</th>
                        <th>Voucher</th>
                        <th>Tổng lượt truy cập</th>
                        <th>Tổng đặt xe</th>
                        <th>Đã sử dụng</th>
                        <th>Trạng thái</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsData.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <strong>{item.link_code}</strong>
                          </td>
                          <td>
                            {item.voucher_code ? (
                              <div>
                                <strong>{item.voucher_code}</strong>
                                {item.voucher_name && (
                                  <div className={cx('voucher-name')}>{item.voucher_name}</div>
                                )}
                              </div>
                            ) : (
                              <span className={cx('no-voucher')}>-</span>
                            )}
                          </td>
                          <td>{parseInt(item.total_visits || 0).toLocaleString('vi-VN')}</td>
                          <td>{parseInt(item.total_bookings || 0).toLocaleString('vi-VN')}</td>
                          <td>
                            {item.max_uses
                              ? `${item.current_uses || 0}/${item.max_uses}`
                              : `${item.current_uses || 0} (không giới hạn)`}
                          </td>
                          <td>
                            <span
                              className={cx('badge', {
                                active: item.is_active,
                                inactive: !item.is_active,
                              })}
                            >
                              {item.is_active ? 'Kích hoạt' : 'Tắt'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PromotionLinkManagement;
